// mon-budget-app/src/pages/ResetPasswordPage.jsx

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { THEMES, THEME_COLORS } from '../components/Constants';
import { authFetch } from '../utils/api';

export default function ResetPasswordPage() {
    const { currentTheme } = useTheme();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];
    
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Récupérer le token et l'email de l'URL
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    useEffect(() => {
        // Vérification initiale du lien (l'expiration sera vérifiée par le backend)
        if (!token || !email) {
            setError("Lien de réinitialisation incomplet. Veuillez refaire une demande.");
        }
    }, [token, email]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (password !== confirmPassword) {
            setError("Les mots de passe ne correspondent pas.");
            return;
        }

        if (!token || !email) {
            setError("Données de réinitialisation manquantes. Impossible de continuer.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await authFetch.post("/reset-password", {
                token, 
                email, 
                newPassword: password 
            });

            const data = await response.json();

            if (!response.ok) {
                // Le backend renvoie une erreur si le jeton est expiré/invalide
                setError(data.error || "Échec de la mise à jour du mot de passe.");
            } else {
                setSuccess(data.message + " Vous pouvez maintenant vous connecter.");
                setPassword('');
                setConfirmPassword('');
                // Note: Laisser l'utilisateur sur cette page avec un lien vers la connexion
            }

        } catch (err) {
            console.error("❌ Erreur réseau lors de la réinitialisation:", err);
            setError("Impossible de contacter le serveur.");
        } finally {
            setIsLoading(false);
        }
    };

    const styles = {
        container: {
            display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", padding: "0 15px",
        },
        card: {
            backgroundColor: themeColors.mainBackground, padding: 30, borderRadius: 20, boxShadow: "0 4px 15px rgba(0,0,0,0.2)", width: "100%", maxWidth: 400, color: themeColors.text,
        },
        title: { color: themeColors.secondary, textAlign: "center", marginBottom: 20 },
        input: { padding: "12px 12px 12px 40px", width: "100%", borderRadius: 10, border: `1px solid ${themeColors.inputBorder}`, boxSizing: "border-box", backgroundColor: themeColors.inputBackground, color: themeColors.text },
        button: { width: "100%", padding: 12, marginTop: 15, backgroundColor: themeColors.secondary, color: "#fff", border: "none", borderRadius: 10, cursor: "pointer", fontWeight: 'bold' },
        inputIcon: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: themeColors.secondary, fontSize: '18px' },
        inputWrapper: { position: "relative", marginBottom: 15 },
        eyeIcon: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer", color: themeColors.secondary, zIndex: 2 },
        link: { color: themeColors.secondary, textDecoration: 'none', fontWeight: 'bold' }
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <h2 style={styles.title}>Définir un nouveau mot de passe</h2>
                <p style={{ color: themeColors.text, textAlign: 'center', marginBottom: 20 }}>
                    E-mail : <strong style={{ color: themeColors.secondary }}>{email}</strong>
                </p>
                <form onSubmit={handleSubmit}>

                    {success ? (
                        <>
                            <p style={{ color: themeColors.success, textAlign: 'center', fontWeight: 'bold', marginBottom: 20 }}>{success}</p>
                            <Link to="/" style={{ textDecoration: 'none' }}>
                                <button type="button" style={{ ...styles.button, backgroundColor: themeColors.success }}>
                                    Aller à la connexion
                                </button>
                            </Link>
                        </>
                    ) : (
                        <>
                            {/* Champ Nouveau Mot de Passe */}
                            <div style={styles.inputWrapper}>
                                <FaLock style={styles.inputIcon} />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    style={styles.input}
                                    placeholder="Nouveau mot de passe"
                                    required
                                    disabled={isLoading}
                                />
                                <span onClick={() => setShowPassword(!showPassword)} style={styles.eyeIcon}>
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>

                            {/* Champ Confirmation */}
                            <div style={styles.inputWrapper}>
                                <FaLock style={styles.inputIcon} />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    style={styles.input}
                                    placeholder="Confirmer le nouveau mot de passe"
                                    required
                                    disabled={isLoading}
                                />
                                <span onClick={() => setShowPassword(!showPassword)} style={styles.eyeIcon}>
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </span>
                            </div>

                            {error && <p style={{ color: themeColors.danger, textAlign: 'center', fontWeight: 'bold' }}>{error}</p>}
                            
                            <button type="submit" disabled={isLoading} style={{ ...styles.button, opacity: isLoading ? 0.6 : 1 }}>
                                {isLoading ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
                            </button>
                        </>
                    )}
                </form>

                <p style={{ textAlign: "center", marginTop: 15, color: themeColors.text }}>
                    <Link to="/" style={styles.link}>Retour à la connexion</Link>
                </p>
            </div>
        </div>
    );
}